export type HelpArticle = {
  id: string
  categoria: string
  icone: string  // emoji
  titulo: string
  resumo: string
  tags: string[]
  conteudo: string  // markdown (suporta :::flow, :::step, :::tip, :::warning, :::success, tabelas)
}

export const HELP_CATEGORIAS = [
  { id: 'comecando', label: 'Começando', icone: '🚀' },
  { id: 'pdv', label: 'PDV / Balcão', icone: '🛒' },
  { id: 'vendas', label: 'Pedidos & Vendas', icone: '🧾' },
  { id: 'carga', label: 'Carga & Entrega', icone: '🚚' },
  { id: 'entregador', label: 'App do Entregador', icone: '📱' },
  { id: 'faturamento', label: 'Faturamento', icone: '💵' },
  { id: 'fiscal', label: 'Fiscal · NF-e · ST/DIFAL', icone: '📜' },
  { id: 'financeiro', label: 'Financeiro', icone: '💰' },
  { id: 'estoque', label: 'Estoque', icone: '📦' },
  { id: 'comodato', label: 'Comodato (Freezers)', icone: '🧊' },
  { id: 'cadastros', label: 'Cadastros', icone: '📋' },
  { id: 'sofia', label: 'Sofia (IA)', icone: '🤖' },
  { id: 'admin', label: 'Configuração', icone: '⚙️' },
]

export const HELP_ARTICLES: HelpArticle[] = [

  // ─────────────────────────────────────────────
  // COMEÇANDO
  // ─────────────────────────────────────────────
  {
    id: 'primeiros-passos',
    categoria: 'comecando',
    icone: '🚀',
    titulo: 'Primeiros passos — visão geral do sistema',
    resumo: 'Entenda o fluxo completo: vender, separar, entregar e receber.',
    tags: ['inicio', 'tour', 'visao geral', 'onboarding', 'fluxo'],
    conteudo: `
# Primeiros passos no VELORE ERP

Bem-vindo! O sistema cuida de tudo: desde a venda até o dinheiro entrar no caixa. O caminho completo é sempre o mesmo:

:::flow
Vender :: balcão (PDV) ou pedido de entrega
Separar :: montar a carga com a Sofia
Entregar :: app do entregador no celular
Faturar :: gera a nota e o financeiro
Receber :: baixa a conta e fecha o caixa
:::

## Por onde começar?

- **Vender no balcão agora?** Veja [Vender no PDV](#pdv-vender)
- **Anotar um pedido pra entregar depois?** Veja [Criar um pedido](#vendas-criar-pedido)
- **Organizar as entregas do dia?** Veja [Montar a carga](#carga-montar)
- **Sou entregador?** Veja [App do Entregador — primeiros passos](#entregador-primeiros-passos)
- **Preciso emitir uma nota fiscal?** Veja [O que é NF-e e como emitir](#fiscal-o-que-e-nfe)

## Navegando pelo sistema

O menu lateral esquerdo agrupa tudo por área: **Vendas**, **Financeiro**, **Estoque**, **Fiscal**, etc. Clique na área que precisa.

A **barra de busca** no topo acha qualquer pedido, cliente ou produto — é a forma mais rápida de encontrar algo.

:::tip Atalho poderoso
Aperte **F5** de qualquer tela para abrir o PDV (caixa rápido) na hora. Não precisa nem ir no menu.
:::

:::info Ficou com dúvida?
Clique no ícone **? Ajuda** no canto superior para abrir a Central de Ajuda. Ou fale com a **Sofia** (o botão roxo no topo) — ela explica em linguagem simples qualquer funcionalidade do sistema.
:::
`.trim(),
  },

  {
    id: 'glossario',
    categoria: 'comecando',
    icone: '📖',
    titulo: 'Glossário — termos do sistema',
    resumo: 'O que significa cada termo: pedido, carga, romaneio, NF-e, DIFAL e mais.',
    tags: ['glossario', 'termos', 'definicoes', 'nfe', 'difal', 'romaneio', 'comodato'],
    conteudo: `
# Glossário de termos

Não precisa saber de contabilidade. Aqui estão os termos que aparecem no dia a dia, explicados de forma simples.

| Termo | O que significa |
|---|---|
| **Pedido** | O registro de uma venda antes de ser faturada. Fica "em aberto" até sair a nota ou o recibo. |
| **Faturar** | Transformar o pedido em oficial: gera a nota (se fiscal), baixa o estoque e cria a conta a receber. |
| **Carga** | Um conjunto de pedidos agrupados pra entrega no mesmo dia e veículo. |
| **Romaneio** | A lista de entregas que o motorista leva no celular. |
| **NF-e** | Nota Fiscal Eletrônica — o documento fiscal emitido na SEFAZ (governo). Tem validade jurídica. |
| **DANFE** | O espelho impresso da NF-e. Acompanha a mercadoria no caminho. |
| **ST** | Substituição Tributária — imposto pago na frente, na saída da fábrica ou do distribuidor. |
| **DIFAL** | Diferença de alíquota de ICMS entre estados. Cai em vendas pra consumidor de outro estado. |
| **GNRE** | Guia de Recolhimento de Receitas Estaduais — o "boleto" do DIFAL pago pro estado de destino. |
| **Comodato** | Empréstimo de freezer ao cliente: ele usa, mas o bem continua sendo da Velore. |
| **Acerto de carga** | Conferência ao fim do dia: dinheiro recebido x itens devolvidos x notas emitidas. |
| **Borderô** | Lista de contas a pagar agrupadas pra pagar de uma vez (ex.: PIX em lote). |
| **Baixa** | Marcar uma conta como paga/recebida. |
| **CFOP** | Código que classifica a natureza da operação fiscal (venda, remessa, devolução etc.). |
| **NCM** | Código de classificação do produto na tabela fiscal brasileira. Obrigatório na NF-e. |
| **PDV** | Ponto de Venda — a tela de caixa rápido para vendas no balcão. |

:::tip Não achou o termo?
Pergunte pra **Sofia** — escreva o termo e ela explica com exemplos do dia a dia da distribuidora.
:::
`.trim(),
  },

  {
    id: 'como-navegar',
    categoria: 'comecando',
    icone: '🗺️',
    titulo: 'Como navegar e buscar no sistema',
    resumo: 'Menu lateral, barra de busca, atalhos de teclado e dicas de agilidade.',
    tags: ['navegacao', 'menu', 'busca', 'atalho', 'f5', 'teclas'],
    conteudo: `
# Como navegar e buscar no sistema

O sistema foi feito pra ser rápido. Aqui estão os caminhos mais curtos para o que você precisa.

## O menu lateral

O menu fica no lado esquerdo da tela. Os grupos principais são:

| Grupo | O que tem lá |
|---|---|
| **Vendas** | Pedidos, PDV, Roteirização, Carga |
| **Financeiro** | Contas a receber/pagar, Borderô, Conciliação, Fluxo de Caixa |
| **Estoque** | Movimentos, Entrada NF, Ajuste, Transferência |
| **Fiscal** | NF-e emitidas, DIFAL, GNRE, Certificado |
| **Comodato** | Freezers, Remessas, Retornos, Dashboard |
| **Cadastros** | Clientes, Produtos, Tabelas de Preço, Fornecedores |
| **Configurações** | Empresa, Usuários, Filiais, Formas de Pagamento |

## A barra de busca do topo

Digite qualquer coisa ali: **número de pedido**, **nome de cliente**, **código de produto**. O sistema acha na hora sem precisar entrar em menu nenhum.

## Atalhos de teclado

| Atalho | O que faz |
|---|---|
| **F5** | Abre o PDV (caixa rápido) de qualquer tela |
| **Enter** | Em formulários, avança pro próximo campo |
| **Esc** | Fecha modais e janelas |

## Colunas nas listas

Nas telas de **Pedidos** e **Vendas**, clique no ícone **⋮ (três pontinhos)** no canto da tabela para ligar ou desligar colunas. Essa configuração fica salva no seu navegador — da próxima vez aparece igual.

Clique no **cabeçalho de qualquer coluna** para ordenar a lista (crescente ou decrescente).

:::tip Filtros por chips
Nas telas de Financeiro, use os chips coloridos no topo (**Atrasados**, **Hoje**, **Semana**, **Mês**) para filtrar sem digitar nada. Mais rápido que qualquer filtro manual.
:::
`.trim(),
  },

  {
    id: 'pedir-ajuda-sofia',
    categoria: 'comecando',
    icone: '💬',
    titulo: 'Como pedir ajuda à Sofia',
    resumo: 'A assistente que explica qualquer função do sistema em linguagem simples.',
    tags: ['sofia', 'ajuda', 'duvida', 'chat', 'assistente', 'ia'],
    conteudo: `
# Como pedir ajuda à Sofia

A **Sofia** é a assistente inteligente do sistema. Você escreve a dúvida do jeito que fala — sem precisar saber o nome técnico das coisas — e ela explica e indica o caminho certo.

:::flow
Abrir a Sofia :: botão roxo no topo da tela
Escrever a dúvida :: do jeito que você fala
Receber a resposta :: passo a passo + onde clicar
Artigo relacionado :: clique pra aprofundar
:::

## Como abrir

Clique no **botão roxo com o ícone da Sofia** no canto superior da tela. Ele aparece em qualquer página. Ou, dentro da Central de Ajuda, clique em **"Pergunte à Sofia"** no campo de chat.

## O que você pode perguntar

- "Como faço uma venda com nota fiscal pra um cliente do Piauí?"
- "Onde fica o ajuste de estoque?"
- "Como dou baixa em várias contas de uma vez?"
- "O que é comodato?"
- "Como o entregador registra uma entrega sem internet?"
- "Como emito a GNRE?"

## Como a Sofia responde

Ela busca nos **artigos da Central de Ajuda** e monta uma resposta personalizada. Se quiser mais detalhes, ela sugere o artigo completo.

:::tip
No final de cada artigo da Central de Ajuda, há um botão **"Abrir chat com a Sofia"**. Clique nele se o artigo não resolveu — sua dúvida já vai aparecer no chat dela.
:::

:::info
A Sofia conhece as funções do sistema, mas não tem acesso aos seus dados (pedidos, clientes, valores). Para questões específicas dos seus registros, use as telas normais do sistema.
:::
`.trim(),
  },

  // ─────────────────────────────────────────────
  // PDV
  // ─────────────────────────────────────────────
  {
    id: 'pdv-vender',
    categoria: 'pdv',
    icone: '🛒',
    titulo: 'Vender no balcão (PDV)',
    resumo: 'Venda rápida no caixa, do produto ao comprovante.',
    tags: ['pdv', 'balcao', 'caixa', 'venda', 'cupom', 'f5'],
    conteudo: `
# Vender no balcão (PDV)

O PDV é a tela de **venda rápida** do caixa. Feita pra ser veloz: você escaneia, escolhe o pagamento e imprime — tudo em menos de um minuto.

:::flow
Abrir o PDV :: tecla F5 ou menu PDV
Buscar produto :: nome ou código de barras
Adicionar ao carrinho :: Enter joga o item
Cliente :: Consumidor Final é o padrão
Finalizar :: escolha a forma de pagamento
Imprimir :: o cupom sai automático
:::

## Passo a passo detalhado

:::step 1. Abrir o PDV
Aperte **F5** de qualquer tela do sistema. Ou clique em **PDV** no menu lateral. A tela do caixa abre na hora.
:::

:::step 2. Buscar e adicionar produtos
No campo de busca do PDV, digite o **nome** do produto ou passe o **leitor de código de barras**. O produto aparece na lista abaixo. Aperte **Enter** (ou clique) para jogar no carrinho.

Repita pra cada produto da venda.
:::

:::step 3. Ajustar quantidade
Clique no produto já adicionado no carrinho. Um painel abre com o campo de **quantidade** — altere e confirme. Também dá pra digitar a quantidade direto antes de apertar Enter no item.
:::

:::step 4. Dar desconto (item ou total)
- **Desconto no item**: clique no item no carrinho e edite o campo **Desconto**.
- **Desconto na venda toda**: no rodapé do carrinho, há o campo **Desconto geral**.
:::

:::step 5. Informar o cliente (opcional)
Por padrão o sistema usa **Consumidor Final** (sem cadastro). Se quiser registrar o cliente, clique em **Trocar cliente** e busque pelo nome ou CPF/CNPJ. Dá pra cadastrar na hora se for novo.
:::

:::step 6. Finalizar e pagar
Clique em **Finalizar**. Escolha a forma de pagamento: **Dinheiro**, **Cartão de Crédito**, **Cartão de Débito**, **PIX** ou outra forma configurada.

Se o cliente vai pagar de formas diferentes (ex.: metade PIX e metade dinheiro), veja [Pagamento múltiplo no PDV](#pdv-pagamento-multiplo).
:::

:::step 7. Imprimir o cupom
Depois de confirmar o pagamento, o **cupom imprime automaticamente** (se a impressora estiver configurada). A venda está concluída.
:::

:::tip Precisou de 2ª via?
Vá em **Vendas do Dia** (botão no topo do PDV) e clique em **Reimprimir** na venda desejada.
:::

:::warning
O PDV é para vendas **no balcão, na hora**. Para pedidos que vão ser entregues depois, use a tela de [Criar Pedido](#vendas-criar-pedido).
:::
`.trim(),
  },

  {
    id: 'pdv-pagamento-multiplo',
    categoria: 'pdv',
    icone: '💳',
    titulo: 'Pagamento múltiplo no PDV',
    resumo: 'Divida o pagamento entre dinheiro, cartão, PIX e outras formas.',
    tags: ['pdv', 'pagamento', 'multiplo', 'dividir', 'troco', 'pix', 'cartao'],
    conteudo: `
# Pagamento múltiplo no PDV

Quando o cliente quer pagar uma parte no cartão e outra no dinheiro (ou qualquer combinação), o sistema divide sem problema.

:::flow
Finalizar a venda :: clique em Finalizar
Adicionar forma :: escolha a 1ª forma e o valor
Adicionar outra :: clique em + Adicionar forma
Confirmar :: o sistema fecha quando bater o total
:::

## Passo a passo

:::step 1. Clique em Finalizar
Quando o carrinho tiver todos os itens, clique em **Finalizar** no rodapé do PDV.
:::

:::step 2. Escolha a primeira forma de pagamento
No modal que abrir, selecione a forma (ex.: **Cartão de Crédito**) e informe o **valor** que será pago nessa forma.

Não precisa preencher o total inteiro agora — só a parte que vai nessa forma.
:::

:::step 3. Adicione a segunda forma
Clique em **+ Adicionar forma de pagamento**. Escolha a próxima (ex.: **Dinheiro**) e informe o valor. O sistema mostra o **saldo restante** pra ajudar.

Repita se tiver uma terceira forma.
:::

:::step 4. Confirmar
Quando o total das formas bater com o valor da venda, o botão **Confirmar** fica disponível. Clique nele pra fechar.
:::

:::tip Troco
Se o cliente deu mais dinheiro do que devia, informe o valor total entregue no campo **Dinheiro** — o sistema calcula o **troco** na hora.
:::
`.trim(),
  },

  {
    id: 'pdv-venda-em-espera',
    categoria: 'pdv',
    icone: '⏸️',
    titulo: 'Venda em espera (segurar e retomar)',
    resumo: 'Coloque uma venda em pausa e retome depois sem perder os itens.',
    tags: ['pdv', 'espera', 'pausar', 'retomar', 'segurar venda'],
    conteudo: `
# Venda em espera

Precisa atender outro cliente rápido sem perder o que já adicionou no carrinho? Use a **venda em espera** — o sistema guarda tudo e você retoma quando quiser.

:::flow
Adicionar itens :: carrinho normal
Colocar em espera :: botão Colocar em Espera
Atender outro :: o PDV limpa pra nova venda
Retomar :: clique em Retomar Venda
:::

## Passo a passo

:::step 1. Montar o carrinho normalmente
Adicione os produtos como numa venda normal.
:::

:::step 2. Colocar em espera
Clique no botão **Colocar em Espera** (fica no topo ou rodapé do PDV, dependendo da resolução). O carrinho some da tela, mas a venda fica salva com status **Em Espera**.
:::

:::step 3. Atender outra venda
O PDV fica limpo pra você começar uma venda nova normalmente.
:::

:::step 4. Retomar a venda em espera
Quando quiser voltar, clique em **Retomar Venda** (ou no banner que aparece no topo do PDV indicando que há uma venda em espera). Os itens voltam todos ao carrinho.
:::

:::tip
A venda em espera **não reserva estoque** e **não gera conta** — só aparece na tela do PDV. Se fechar o navegador sem finalizar, ela pode ser perdida.
:::

:::warning
Não confunda com **cancelar**. Cancelar apaga a venda. Em espera apenas pausa.
:::
`.trim(),
  },

  {
    id: 'pdv-fechamento',
    categoria: 'pdv',
    icone: '🔒',
    titulo: 'Fechamento de caixa do PDV',
    resumo: 'Como fechar o caixa do dia e ver as vendas realizadas.',
    tags: ['pdv', 'fechamento', 'caixa', 'vendas do dia', 'relatorio', 'reimprimir'],
    conteudo: `
# Fechamento de caixa do PDV

No fim do turno ou do dia, é hora de conferir as vendas e fechar o caixa.

:::flow
Vendas do dia :: veja o resumo do que foi vendido
Conferir :: totais por forma de pagamento
Fechar caixa :: registra o fechamento
Reimprimir :: 2ª via de qualquer cupom
:::

## Passo a passo

:::step 1. Acessar as Vendas do Dia
No PDV, clique no botão **Vendas do Dia** (fica no topo da tela do PDV). Aparece a lista de todas as vendas do turno atual.
:::

:::step 2. Conferir os totais
A tela mostra:
- Total de vendas no período
- Quantidade de transações
- Subtotal por forma de pagamento (dinheiro, cartão, PIX etc.)

Compare com o dinheiro físico no caixa.
:::

:::step 3. Reimprimir um cupom
Localize a venda na lista e clique em **Reimprimir**. O cupom é gerado de novo na impressora.
:::

:::step 4. Fechar o caixa
Clique em **Fechar Caixa**. O sistema pede confirmação e registra o fechamento com data, hora e operador. Depois disso, o caixa do dia está encerrado.
:::

:::tip
Se usar filiais, o fechamento pode ser consolidado por filial. A filial **fiscal** cuida da nota; a filial **financeira** recebe o lançamento no caixa.
:::
`.trim(),
  },

  // ─────────────────────────────────────────────
  // VENDAS / PEDIDOS
  // ─────────────────────────────────────────────
  {
    id: 'vendas-criar-pedido',
    categoria: 'vendas',
    icone: '🧾',
    titulo: 'Criar um pedido de venda',
    resumo: 'Anote um pedido para entrega ou faturamento posterior.',
    tags: ['pedido', 'venda', 'novo pedido', 'cliente', 'itens', 'desconto'],
    conteudo: `
# Criar um pedido de venda

O **pedido** é diferente do PDV: você registra a venda agora, mas a entrega e o faturamento acontecem depois. É o fluxo principal de uma distribuidora.

:::flow
Menu Vendas :: clique em Novo Pedido
Cliente :: quem vai comprar
Itens :: produtos e quantidades
Forma e prazo :: como vai pagar
Salvar :: pedido fica em aberto
:::

## Passo a passo

:::step 1. Abrir Novo Pedido
No menu lateral, clique em **Vendas** e depois em **Novo Pedido** (ou no botão **+ Novo** na lista de pedidos).
:::

:::step 2. Escolher o cliente
Comece digitando o **nome ou CPF/CNPJ** do cliente no campo de busca. Se o cliente é novo, clique em **+ Cadastrar cliente** ali mesmo — não precisa sair da tela.

A **UF do cliente** é muito importante: vendas pra fora do Ceará podem ter imposto extra. Veja [NF-e, ST e DIFAL](#fiscal-nfe-st-difal).
:::

:::step 3. Adicionar itens
No campo de produto, busque por **nome** ou **código de barras**. Informe a **quantidade**. O **preço de tabela** já vem preenchido — dá pra editar se o vendedor tiver autorização pra desconto.

Repita pra cada produto.
:::

:::step 4. Desconto e tipo de venda
- **Desconto por item**: na linha do produto.
- **Desconto geral**: no rodapé do pedido.
- **Tipo de venda**: **Fiscal** (vai gerar NF-e) ou **Simples** (controle interno, sem nota). Pode deixar pra decidir no faturamento.
- **Vendedor**: selecione quem realizou a venda (importante pra relatórios e metas).
:::

:::step 5. Forma de pagamento e prazo
Escolha a **forma de pagamento** (boleto, PIX, dinheiro etc.) e o **prazo** (à vista, 7 dias, 30/60/90 dias etc.). Isso vai definir as parcelas da conta a receber quando faturar.
:::

:::step 6. Salvar
Clique em **Salvar**. O pedido fica com status **Em aberto**. A partir daí:
- Pode entrar numa **carga** pra entrega (veja [Montar a carga](#carga-montar))
- Pode ser **faturado** direto (veja [Faturar um pedido](#faturar-pedido))
:::

:::tip Colunas da lista
Na tela de Pedidos, clique em **⋮** pra ligar/desligar colunas: Cód. Vendedor, Rota, UF, Canal, Total. A configuração fica salva.
:::
`.trim(),
  },

  {
    id: 'vendas-lista-filtros',
    categoria: 'vendas',
    icone: '🔍',
    titulo: 'Lista de pedidos — filtros e colunas',
    resumo: 'Como encontrar pedidos, ordenar, filtrar e personalizar as colunas.',
    tags: ['pedidos', 'lista', 'filtro', 'busca', 'colunas', 'ordenar', 'status'],
    conteudo: `
# Lista de pedidos — filtros e colunas

A tela de **Vendas → Pedidos** mostra todos os pedidos. Saiba como encontrar o que procura rápido.

## Filtros disponíveis

Use os campos no topo da lista:

| Filtro | Como usar |
|---|---|
| **Busca** | Nome do cliente, número do pedido ou código do produto |
| **Status** | Em aberto, Faturado, Cancelado, Em separação |
| **Período** | Data de criação ou de faturamento |
| **Vendedor** | Filtre pelos pedidos de um vendedor específico |
| **Tipo** | Fiscal ou Simples |
| **UF** | Filtre por estado do cliente (útil pra ver vendas interestaduais) |

## Ordenar a lista

Clique no **cabeçalho de qualquer coluna** para ordenar por ela. Clique de novo para inverter (crescente/decrescente).

## Ligar e desligar colunas

Clique no ícone **⋮** no canto superior direito da tabela. Uma lista de colunas aparece — marque ou desmarque conforme sua necessidade:

- **Cliente**, **Data**, **Tipo**, **Rota**, **UF**, **Cód. Vendedor**, **Canal**, **Total**, **Status**

Essa configuração fica salva no navegador — fica igual da próxima vez.

## Ver os itens de um pedido

Clique na **seta ou linha do pedido** pra expandir e ver os produtos, quantidades e preços daquele pedido sem precisar abrir uma nova tela.

:::tip
Quer ver só os pedidos atrasados ou de uma rota específica? Use o filtro de **Rota** combinado com o de **Status**. O resultado é a lista exata pra passar pro motorista.
:::
`.trim(),
  },

  {
    id: 'vendas-editar-cancelar',
    categoria: 'vendas',
    icone: '✏️',
    titulo: 'Editar ou cancelar um pedido',
    resumo: 'Como corrigir um pedido já salvo ou cancelá-lo sem bagunçar o estoque.',
    tags: ['pedido', 'editar', 'cancelar', 'estornar', 'excluir', 'corrigir'],
    conteudo: `
# Editar ou cancelar um pedido

Errou algo no pedido? Sem problema. O sistema permite **editar** ou **cancelar** com segurança — tudo é revertido automaticamente.

## Editar um pedido

:::step 1. Abrir o pedido
Na lista de Pedidos, clique no pedido que deseja corrigir. Depois clique em **Editar**.
:::

:::step 2. O que acontece por baixo
O sistema faz automaticamente:
1. **Estorna** o estoque e as contas que já tinham sido geradas
2. Abre o pedido pra edição
3. Ao salvar, **recria** tudo com os dados novos

Você não precisa fazer nada manualmente — só editar os campos e salvar.
:::

:::step 3. Fazer as correções
Altere o que precisar: cliente, itens, quantidades, preços, forma de pagamento, tipo. Depois clique em **Salvar**.
:::

:::warning
Pedidos que já foram **faturados com NF-e emitida** não podem ser editados diretamente. Para corrigir, será necessário cancelar ou emitir carta de correção. Consulte [Cancelar nota e carta de correção](#fiscal-cancelar-nfe).
:::

## Cancelar um pedido

:::step Cancelar
Abra o pedido e clique em **Cancelar Pedido**. O sistema pede confirmação e então:
- **Reverte o estoque** (devolve as quantidades)
- **Cancela as contas a receber** geradas (não deleta, só cancela)
- Marca o pedido como **Cancelado**
:::

:::tip
Cancelado não é apagado — o pedido ainda aparece na lista com status **Cancelado** pra fins de histórico. Se precisar apagar de verdade, consulte um administrador.
:::

## Bonificação (brinde)

Para um pedido do tipo **bonificação** (quando você dá produto de graça pro cliente), use o **Tipo 5 ou 6** ao criar o pedido. O CFOP já é preenchido corretamente (5910 / 6910) e o pedido não gera conta a receber.
`.trim(),
  },

  {
    id: 'vendas-relatorio',
    categoria: 'vendas',
    icone: '📊',
    titulo: 'Relatório de vendas em PDF',
    resumo: 'Gere o relatório do período com totais por vendedor, produto e cliente.',
    tags: ['relatorio', 'vendas', 'pdf', 'resumo', 'totais', 'vendedor'],
    conteudo: `
# Relatório de vendas em PDF

O sistema gera relatórios de venda prontos pra imprimir ou arquivar.

:::flow
Menu Vendas :: clique em Relatórios
Filtrar :: período, vendedor, tipo
Gerar :: clique em Gerar Relatório
PDF :: abre no navegador pra salvar ou imprimir
:::

## Passo a passo

:::step 1. Acessar Relatórios
No menu **Vendas**, clique em **Relatórios**. Ou, na lista de Pedidos, procure o botão **Relatório PDF** no topo.
:::

:::step 2. Definir os filtros
Escolha:
- **Período**: data de início e fim
- **Vendedor**: todos ou um específico
- **Tipo de venda**: fiscal, simples ou ambos
- **Status**: faturados, cancelados ou todos
:::

:::step 3. Gerar o PDF
Clique em **Gerar Relatório**. O arquivo abre em uma nova aba do navegador. Use **Ctrl+P** (ou o botão imprimir do navegador) pra imprimir, ou **Ctrl+S** pra salvar.
:::

## O que o relatório mostra

- Listagem de todos os pedidos do período com cliente, valor, tipo e status
- **Totais por vendedor**
- **Totais por forma de pagamento**
- Subtotal geral do período

:::tip
Para o relatório de margem e DRE, acesse **Financeiro → Controladoria**. Lá tem gráficos de evolução de vendas e margem por período.
:::
`.trim(),
  },

  // ─────────────────────────────────────────────
  // CARGA
  // ─────────────────────────────────────────────
  {
    id: 'carga-montar',
    categoria: 'carga',
    icone: '🚚',
    titulo: 'Montar a carga com a Sofia',
    resumo: 'Como a Sofia organiza os pedidos por rota e veículo para o dia de entrega.',
    tags: ['carga', 'roteirizacao', 'sofia', 'rota', 'veiculo', 'pedidos', 'or-tools'],
    conteudo: `
# Montar a carga com a Sofia

A carga é o **conjunto de pedidos** que saem juntos num veículo. A **Sofia** usa inteligência artificial pra organizar automaticamente a melhor ordem de entrega.

:::flow
Selecionar pedidos :: os que vao pra rua hoje
Sofia monta :: agrupa por rota e veículo
Confirmar :: aprova o agrupamento
Imprimir separação :: lista do que tirar do estoque
Romaneio :: vai pro entregador
:::

## Passo a passo

:::step 1. Acessar Roteirização
No menu **Vendas**, clique em **Roteirização** (ou **Carga**).
:::

:::step 2. Selecionar os pedidos
A lista mostra todos os pedidos com status **Em aberto**. Marque os que vão sair no dia. Dá pra filtrar por **rota**, **vendedor** ou **UF** pra facilitar.
:::

:::step 3. Pedir pra Sofia montar
Clique em **Montar Carga com a Sofia** (ou **Roteirizar**). A Sofia:
- Agrupa os pedidos por **rota** e **veículo**
- Sugere a **ordem de entrega** mais eficiente
- Mostra o **resumo por carga** (peso, volumes, valor)
:::

:::step 4. Revisar e confirmar
Veja o resultado sugerido. Se quiser mudar alguma coisa (trocar pedido de veículo, reordenar parada), ajuste manualmente. Depois clique em **Confirmar**.
:::

:::step 5. Imprimir a separação
Com a carga confirmada, clique em **Imprimir Separação**. Essa lista vai pra equipe do estoque separar os produtos físicos.
:::

:::step 6. Gerar o romaneio
Clique em **Gerar Romaneio**. O entregador recebe no celular (pelo [app](#entregador-primeiros-passos)) a lista de entregas na ordem certa.
:::

:::tip Cadastros necessários
Pra roteirização funcionar bem, tenha [veículos](#carga-veiculos-motoristas) e [motoristas](#carga-veiculos-motoristas) cadastrados, e os clientes com **rota** definida no cadastro.
:::
`.trim(),
  },

  {
    id: 'carga-veiculos-motoristas',
    categoria: 'carga',
    icone: '🚛',
    titulo: 'Cadastrar veículos e motoristas',
    resumo: 'Cadastre os caminhões e entregadores antes de montar cargas.',
    tags: ['veiculo', 'motorista', 'cadastro', 'carga', 'entregador', 'placa'],
    conteudo: `
# Cadastrar veículos e motoristas

Antes de montar cargas, você precisa ter os **veículos** e **motoristas** cadastrados no sistema.

## Cadastrar veículo

:::step 1. Acessar
Vá em **Cadastros → Veículos** (ou **Carga → Veículos**).
:::

:::step 2. Preencher os dados
Clique em **+ Novo Veículo** e preencha:
- **Placa**
- **Descrição** (ex.: "Caminhão Iveco Branco")
- **Capacidade** (peso em kg ou volume)
- **Filial** responsável

Clique em **Salvar**.
:::

## Cadastrar motorista / entregador

:::step 1. Acessar
Vá em **Cadastros → Motoristas** (ou **Entregadores**).
:::

:::step 2. Preencher os dados
Clique em **+ Novo Motorista** e preencha:
- **Nome completo**
- **CPF** (é o login do app do entregador)
- **PIN** numérico (a senha do app — escolha um número fácil de lembrar pra ele)
- **Veículo padrão** (opcional)

Clique em **Salvar**.
:::

:::tip
O **CPF + PIN** são as credenciais que o motorista vai usar pra entrar no [App do Entregador](#entregador-primeiros-passos). Anote e passe pra ele antes da primeira entrega.
:::

:::warning
Não coloque PIN óbvio como "1234" ou o próprio CPF. Escolha algo que só o motorista saiba.
:::
`.trim(),
  },

  {
    id: 'carga-acerto',
    categoria: 'carga',
    icone: '✅',
    titulo: 'Acerto da carga (conferência do dia)',
    resumo: 'Confira o dinheiro e os itens devolvidos quando a carga volta.',
    tags: ['acerto', 'carga', 'conferencia', 'devolucao', 'sobra', 'falta', 'financeiro'],
    conteudo: `
# Acerto da carga

Quando o entregador volta, é hora de conferir se o que saiu voltou certo — tanto em **dinheiro** quanto em **mercadoria**.

:::flow
Carga chegou :: entregador voltou
Acerto :: Financeiro ou Carga → Acerto
Conferir dinheiro :: quanto entrou por forma
Conferir devoluções :: o que voltou físico
Fechar :: sobra ou falta vira lançamento
:::

## Passo a passo

:::step 1. Acessar o Acerto
Vá em **Financeiro → Acerto de Cargas** ou na tela de **Carga**, clique em **Fazer Acerto** na carga correspondente.
:::

:::step 2. Conferir o dinheiro recebido
O sistema mostra o **total esperado** (soma das contas a receber daquela carga). Informe o que o entregador entregou de fato:
- Quanto em **dinheiro**
- Quanto em **PIX**
- Quanto em **cheque** (se houver)

O sistema calcula a diferença automaticamente.
:::

:::step 3. Conferir mercadorias devolvidas
Se algum cliente não recebeu (estava fechado, cancelou na hora), o produto volta físico. Registre a **devolução** no acerto — o sistema devolve o item ao estoque.
:::

:::step 4. Fechar o acerto
Revise o resumo e clique em **Fechar Acerto**. Qualquer **sobra ou falta** vira um lançamento numa conta gerencial para o responsável tratar.
:::

:::tip MDF-e
Para cargas interestaduais, é necessário o **MDF-e** (Manifesto de Documentos Fiscais). O sistema gera o MDF-e junto com a carga. Veja [Fiscal → MDF-e](#fiscal-nfe-st-difal) se precisar de detalhes.
:::
`.trim(),
  },

  // ─────────────────────────────────────────────
  // ENTREGADOR
  // ─────────────────────────────────────────────
  {
    id: 'entregador-primeiros-passos',
    categoria: 'entregador',
    icone: '📱',
    titulo: 'App do Entregador — primeiros passos',
    resumo: 'Como instalar o app, fazer login e aceitar o romaneio do dia.',
    tags: ['app', 'entregador', 'motorista', 'login', 'cpf', 'pin', 'pwa', 'apk'],
    conteudo: `
# App do Entregador — primeiros passos

O app do entregador foi feito pra ser **simples**: botões grandes, PIN numérico, uma ação por tela. Funciona até sem internet.

:::flow
Instalar :: PWA ou APK
Login :: CPF + PIN
Aceitar romaneio :: lista de entregas do dia
Pronto :: comecar as entregas
:::

## Instalar o app

Existem duas formas de usar o app:

**Opção 1 — PWA (pelo navegador, sem instalar)**
1. Abra o **Chrome** no celular
2. Acesse o endereço do sistema seguido de **/m/entregador**
3. Toque em **"Adicionar à tela inicial"** quando o Chrome sugerir
4. O ícone aparece na tela do celular igual a um app normal

**Opção 2 — APK (arquivo de instalação Android)**
1. Peça ao administrador o arquivo **APK do app Velore Entregador**
2. No celular, abra o arquivo e clique em **Instalar**
3. Se pedir permissão pra instalar de fontes desconhecidas, autorize só pra essa instalação

:::step Fazer login
Abra o app. Aparece dois campos:
- **CPF**: digite o CPF do motorista (só números)
- **PIN**: o número que o escritório cadastrou pra você

Toque em **Entrar**.
:::

:::step Aceitar o romaneio
Depois do login, aparece o **romaneio do dia** — a lista de entregas. Leia e toque em **Aceitar** pra começar. Se não houver romaneio, entre em contato com o escritório.
:::

:::tip
Se o app pedir atualização, aceite. Ele atualiza rapidinho e suas entregas ficam salvas.
:::
`.trim(),
  },

  {
    id: 'entregador-fazer-entrega',
    categoria: 'entregador',
    icone: '📦',
    titulo: 'Fazer uma entrega pelo app',
    resumo: 'Passo a passo de cada entrega: foto, GPS, pagamento e NF no WhatsApp.',
    tags: ['entregador', 'entrega', 'foto', 'gps', 'pagamento', 'nf', 'whatsapp'],
    conteudo: `
# Fazer uma entrega pelo app

Para cada cliente na lista, siga esses passos. O app guia você — é uma coisa de cada vez.

:::flow
Check-in :: toca em Iniciar Rota
Chegar ao cliente :: abre a entrega
Registrar :: foto + GPS
Pagamento :: confirma ou troca a forma
Nota fiscal :: gera e manda no WhatsApp
Próximo :: vai pro próximo cliente
:::

## Passo a passo

:::step 1. Iniciar a rota (check-in)
Na tela do romaneio, toque em **Iniciar Rota**. O app registra o horário de saída.
:::

:::step 2. Abrir a entrega do cliente
A lista mostra os clientes em ordem de rota. Toque no nome do cliente que você está atendendo agora.
:::

:::step 3. Tirar a foto
Toque em **Tirar Foto** e fotografe o comprovante de entrega (ou o produto sendo entregue, conforme orientação da empresa). A foto fica vinculada à entrega.
:::

:::step 4. Confirmar o GPS
O app registra a **localização** automaticamente no momento da foto. Não precisa fazer nada — só precisa ter o GPS do celular ligado.
:::

:::step 5. Registrar o pagamento
Aparece a forma de pagamento prevista (ex.: boleto). Se o cliente pagou diferente:
- Toque em **Trocar forma de pagamento**
- Escolha a nova forma (ex.: dinheiro, PIX)
- Se PIX ou dinheiro, confirme o valor

Se o cliente **não pagou** (vai pagar no boleto depois, por exemplo), confirme como "Em aberto".
:::

:::step 6. Gerar a nota e mandar no WhatsApp
Toque em **Gerar NF** (se a entrega for fiscal). A nota é emitida. Depois toque em **Enviar pelo WhatsApp** — o app abre o WhatsApp com a nota em PDF já anexada no número do cliente.
:::

:::step 7. Confirmar entrega e ir pro próximo
Toque em **Confirmar Entrega**. O cliente some da lista de pendentes. Vá pro próximo.
:::

:::success Sem internet? Sem problema
O app salva tudo localmente e **envia automaticamente** quando a internet voltar. Pode entregar em área sem sinal tranquilo — os dados não se perdem.
:::
`.trim(),
  },

  {
    id: 'entregador-caixa',
    categoria: 'entregador',
    icone: '💵',
    titulo: 'Caixa do entregador (fim do dia)',
    resumo: 'Como fechar o caixa no app e entregar o dinheiro ao escritório.',
    tags: ['entregador', 'caixa', 'fim do dia', 'acerto', 'dinheiro', 'resumo'],
    conteudo: `
# Caixa do entregador (fim do dia)

Quando terminar as entregas, você faz o **caixa no próprio app**. Esse resumo vai direto pro escritório pra conferência.

:::flow
Entregas concluidas :: todos os clientes visitados
Abrir caixa :: botão Fechar Meu Caixa
Ver o resumo :: total por forma de pagamento
Confirmar :: dados vao pro escritório
:::

## Passo a passo

:::step 1. Terminar todas as entregas
Conclua ou registre todas as paradas da lista. Mesmo as que o cliente não estava (registre como "não entregue" com o motivo).
:::

:::step 2. Abrir o caixa
Na tela principal do app (após as entregas), toque em **Fechar Meu Caixa** (ou **Caixa do Dia**).
:::

:::step 3. Conferir o resumo
O app mostra:
- Total recebido em **dinheiro**
- Total recebido em **PIX**
- Total de entregas **confirmadas**
- Itens **não entregues** (devolvidos)

Confira se bate com o que está no seu bolso.
:::

:::step 4. Confirmar e entregar
Toque em **Confirmar Caixa**. Os dados vão pro escritório. Agora é só entregar o dinheiro físico pro responsável e ele faz o [acerto da carga](#carga-acerto) no sistema.
:::

:::tip
Se ficou alguma coisa pra sincronizar (icon de nuvem com seta), espere numa área com internet antes de confirmar o caixa. Assim tudo chega completo pro escritório.
:::
`.trim(),
  },

  // ─────────────────────────────────────────────
  // FATURAMENTO
  // ─────────────────────────────────────────────
  {
    id: 'faturar-pedido',
    categoria: 'faturamento',
    icone: '💵',
    titulo: 'Faturar um pedido',
    resumo: 'Transforma o pedido em nota + conta a receber + baixa de estoque.',
    tags: ['faturar', 'faturamento', 'nota', 'conta a receber', 'baixa estoque', 'nfe'],
    conteudo: `
# Faturar um pedido

**Faturar** é o momento em que o pedido vira oficial. O sistema faz tudo de uma vez: emite a nota (se for fiscal), cria a conta a receber e dá baixa no estoque.

:::flow
Pedido em aberto :: separado ou pronto pra entregar
Escolher tipo :: fiscal (NF-e) ou simples
Confirmar :: NF emitida + conta criada + estoque baixado
:::

## Passo a passo

:::step 1. Onde faturar
Existem três caminhos:
- **Pela lista de Pedidos**: abra o pedido e clique em **Faturar**
- **Pela tela de Ondas/Faturamento**: selecione vários pedidos e fature em lote
- **Pela Carga**: ao fechar a carga, clique em **Gerar NF** — fatura todos os pedidos da carga de uma vez
:::

:::step 2. Fiscal ou Simples?
- **Fiscal**: emite uma **NF-e real** na SEFAZ. O cliente recebe a nota. Use pra vendas regulares.
- **Simples**: sem nota (controle interno). Use pra clientes que não precisam de nota ou pra vendas internas.
:::

:::step 3. Revisar forma de pagamento e prazo
Confirme a forma de pagamento e o prazo. Eles definem as parcelas da conta a receber. Ex.: "Boleto 30/60 dias" cria duas parcelas.
:::

:::step 4. Confirmar
Clique em **Confirmar Faturamento** (ou **Emitir NF**). O sistema:
1. Emite a NF-e na SEFAZ (se for fiscal)
2. Cria a conta a receber no financeiro
3. Dá baixa no estoque
4. Muda o status do pedido para **Faturado**
:::

:::warning Atenção — emissão real
Em ambiente de produção, a NF-e tem **valor fiscal real**. Cada nota queima um número. Confira cliente, itens e valores antes de confirmar. Nota emitida errada precisa de cancelamento ou carta de correção.
:::

:::tip Faturamento em lote
Na tela **Ondas / Faturamento**, você seleciona vários pedidos de uma vez e fatura todos juntos. Economiza muito tempo quando há muitos pedidos do dia.
:::
`.trim(),
  },

  {
    id: 'faturamento-formas-prazo',
    categoria: 'faturamento',
    icone: '📅',
    titulo: 'Formas de pagamento e prazos',
    resumo: 'Como funcionam as condições de pagamento e as parcelas nas vendas.',
    tags: ['pagamento', 'prazo', 'parcelas', 'boleto', 'pix', 'condicao', 'faturamento'],
    conteudo: `
# Formas de pagamento e prazos

A forma e o prazo definem **quando e como** o cliente vai pagar — e criam as parcelas certas no financeiro.

## Formas de pagamento disponíveis

As formas são configuradas pelo administrador (veja [Formas de pagamento](#admin-formas-pagamento)). As mais comuns:

| Forma | Quando usar |
|---|---|
| **Dinheiro** | Pagamento na entrega |
| **PIX** | Transferência instantânea |
| **Boleto F1** | Boleto simples, 1 vencimento |
| **Boleto F2** | Dois vencimentos (ex.: 30+60 dias) |
| **Boleto F3** | Três ou mais vencimentos |
| **Cartão** | Débito ou crédito |
| **Cheque** | A prazo |

## Prazos e parcelamento

Ao faturar, o campo **Prazo / Condição de pagamento** define os vencimentos:

- **À vista**: cria 1 parcela pra hoje
- **30 dias**: 1 parcela 30 dias após a emissão
- **30/60 dias**: 2 parcelas (metade em 30, metade em 60 dias)
- **30/60/90 dias**: 3 parcelas iguais

O sistema cria automaticamente as **contas a receber** com as datas certas.

:::step Como escolher
No momento do faturamento (ou na criação do pedido), selecione a forma e o prazo nos campos correspondentes. O sistema calcula as parcelas e mostra o resumo antes de confirmar.
:::

:::tip Boleto CNAB
Para gerar o arquivo de remessa bancária dos boletos, veja [Emissão de boletos e CNAB](#financeiro-boletos).
:::
`.trim(),
  },

  // ─────────────────────────────────────────────
  // FISCAL
  // ─────────────────────────────────────────────
  {
    id: 'fiscal-o-que-e-nfe',
    categoria: 'fiscal',
    icone: '📜',
    titulo: 'O que é NF-e e como emitir',
    resumo: 'Nota Fiscal Eletrônica explicada do zero — o que é, pra que serve e como emitir.',
    tags: ['nfe', 'nota fiscal', 'emitir', 'sefaz', 'danfe', 'basico'],
    conteudo: `
# O que é NF-e e como emitir

A **NF-e** (Nota Fiscal Eletrônica) é o documento oficial de uma venda. Ela é enviada ao governo (SEFAZ) e tem validade jurídica. Sem ela, a mercadoria não pode circular legalmente.

:::flow
Pedido faturado :: ao faturar fiscal
Sistema envia :: vai pra SEFAZ automaticamente
SEFAZ autoriza :: retorna com código de autorização
NF aprovada :: status cStat 100
DANFE :: impresso ou enviado ao cliente
:::

## O que é cada coisa

| Termo | O que é |
|---|---|
| **NF-e** | A nota em formato eletrônico (arquivo XML) |
| **SEFAZ** | O órgão do governo que autoriza a nota |
| **cStat 100** | Código que significa "nota autorizada com sucesso" |
| **DANFE** | O espelho impresso da nota (o papel que acompanha a carga) |
| **Chave de acesso** | O número de 44 dígitos que identifica a nota no governo |

## Como emitir

A NF-e é emitida **automaticamente** quando você fatura um pedido como **Fiscal**. Você não precisa fazer nada extra além de clicar em **Faturar → Fiscal**.

Se quiser ver as notas emitidas, vá em **Fiscal → NF-e Emitidas**.

## Como ver o DANFE

Na tela de NF-e Emitidas, localize a nota e clique em **DANFE**. O PDF abre no navegador pra impressão.

:::warning Ambiente de produção
As notas emitidas no sistema são **reais** — valem na SEFAZ. Não existe "nota de teste" em produção. Confira tudo antes de emitir.
:::

:::tip Homologação x Produção
Se o administrador configurou o sistema em **Homologação**, as notas são de teste e não têm valor fiscal. Ideal pra treinar. Veja [Ambiente fiscal](#admin-config-empresa).
:::
`.trim(),
  },

  {
    id: 'fiscal-nfe-st-difal',
    categoria: 'fiscal',
    icone: '🗺️',
    titulo: 'ST e DIFAL — venda pra outro estado',
    resumo: 'Imposto extra em venda interestadual para consumidor final, explicado simples.',
    tags: ['st', 'difal', 'gnre', 'interestadual', 'piaui', 'icms', 'imposto'],
    conteudo: `
# ST e DIFAL — venda pra outro estado

Venda **dentro do Ceará** é o caso normal. Venda pra um cliente de **outro estado** (pessoa física / consumidor final) tem um imposto extra chamado **DIFAL**.

## Por que existe o DIFAL?

Cada estado tem uma alíquota de ICMS diferente. Quando você vende do Ceará pro Piauí, por exemplo, a diferença entre a alíquota do Piauí (destino) e a alíquota interestadual fica devida ao Piauí. A Velore paga isso por uma guia chamada **GNRE**.

## Exemplo prático — sorvete pro Piauí

| Componente | Valor |
|---|---|
| Alíquota interna do PI | 22,5% |
| Alíquota interestadual CE→PI | 12% |
| FCP (Fundo de Combate à Pobreza) do PI | 2% |
| **DIFAL** | 22,5% − 12% = 10,5% + 2% FCP |

O sistema **calcula tudo automaticamente** ao emitir a nota, com base na UF do cliente.

:::step Como o sistema calcula
Ao criar ou faturar um pedido com cliente de outro estado, o sistema:
1. Identifica a **UF do cliente** no cadastro
2. Busca as **alíquotas configuradas** (Fiscal → Apuração DIFAL)
3. Calcula o DIFAL e inclui na NF-e automaticamente
:::

:::step Onde ajustar as alíquotas
Se a lei de um estado mudar, vá em **Fiscal → Apuração DIFAL**. Encontre o estado e edite:
- **Alíquota interna** do estado
- **FCP** (se aplicável)

O cálculo se atualiza pra próxima nota.
:::

:::step Gerar a GNRE (guia de pagamento)
Após emitir as notas do período, vá em **Fiscal → GNRE**. O sistema agrupa o DIFAL por estado e gera a guia em PDF. Ela automaticamente vira uma **conta a pagar** no financeiro.
:::

:::tip Como saber se uma venda tem DIFAL?
Na lista de Pedidos, ligue a coluna **UF**. Qualquer UF diferente de **CE** pode gerar DIFAL (depende de o cliente ser consumidor final ou revendedor com IE).
:::
`.trim(),
  },

  {
    id: 'fiscal-cancelar-nfe',
    categoria: 'fiscal',
    icone: '❌',
    titulo: 'Cancelar NF-e e carta de correção',
    resumo: 'Como cancelar uma nota emitida errada e quando usar a carta de correção.',
    tags: ['cancelar', 'nota', 'nfe', 'carta de correcao', 'evento', 'sefaz'],
    conteudo: `
# Cancelar NF-e e carta de correção

Emitiu uma nota errada? Existem duas saídas: **cancelamento** (apaga a nota) ou **carta de correção** (corrige dados menores sem cancelar).

## Quando cancelar x quando corrigir?

| Situação | O que fazer |
|---|---|
| Nota do cliente errado | Cancelar e emitir nova |
| Valor errado | Cancelar (dentro de 24h) ou carta de correção |
| Endereço do cliente errado | Carta de correção |
| Erro de item | Cancelar e emitir nova |
| Erro de CFOP | Carta de correção |

:::warning Prazo de cancelamento
O cancelamento só é possível em até **24 horas** após a autorização da nota. Depois disso, é necessário emitir uma nota de devolução.
:::

## Como cancelar uma NF-e

:::step 1. Localizar a nota
Vá em **Fiscal → NF-e Emitidas**. Encontre a nota pelo número, data ou cliente.
:::

:::step 2. Cancelar
Clique na nota e depois em **Cancelar NF-e**. O sistema pede o **motivo do cancelamento** (mínimo 15 caracteres). Preencha e confirme.
:::

:::step 3. O que acontece
- A nota é marcada como **Cancelada** no sistema e na SEFAZ
- O estoque é **revertido** (os produtos voltam)
- A conta a receber é **cancelada**
- Você pode emitir uma nova nota correta
:::

## Como emitir carta de correção

:::step 1. Localizar a nota
Vá em **Fiscal → NF-e Emitidas** e abra a nota com o erro.
:::

:::step 2. Carta de Correção
Clique em **Carta de Correção**. Preencha o campo **Correção** descrevendo o que está errado e o que deveria ser (ex.: "Endereço do destinatário: Rua correta é Rua das Flores, 100"). Confirme.
:::

:::tip
A carta de correção **não pode** corrigir: dados do emitente, destinatário, item ou valor. Só dados de menor relevância fiscal. Dúvida? Consulte o contador.
:::
`.trim(),
  },

  {
    id: 'fiscal-certificado-numeracao',
    categoria: 'fiscal',
    icone: '🔐',
    titulo: 'Certificado A1 e numeração da NF-e',
    resumo: 'Como subir o certificado digital e configurar a série e o próximo número da nota.',
    tags: ['certificado', 'a1', 'numeracao', 'serie', 'proximo numero', 'admin', 'fiscal'],
    conteudo: `
# Certificado A1 e numeração da NF-e

O **certificado digital A1** é a "assinatura eletrônica" da empresa. Sem ele, nenhuma NF-e é emitida.

## Subir o certificado A1

:::step 1. Acessar Configurações da Empresa
Vá em **Configurações → Empresa**. Clique na aba **Fiscal**.
:::

:::step 2. Fazer upload do certificado
Clique em **Enviar Certificado A1**. Selecione o arquivo **.pfx** (ou **.p12**) no seu computador.

Depois informe a **senha do certificado** no campo correspondente. Clique em **Salvar**.
:::

:::step 3. Verificar
O sistema confirma se o certificado foi aceito e mostra a **validade** (data de expiração). Fique atento pra renovar antes do vencimento — nota não sai com certificado vencido.
:::

:::warning Guarde a senha do certificado
Sem a senha, não é possível usar o certificado. Guarde em local seguro — não no próprio computador.
:::

## Configurar a numeração da NF-e

:::step 1. Próximo número
Na aba **Fiscal** de Configurações da Empresa, o campo **Próximo número da NF-e** define de onde a próxima nota vai começar. Só **administradores** conseguem editar.
:::

:::step 2. Quando usar
- Quando migrar do sistema antigo: coloque o número seguinte à última nota emitida lá
- Se houver erro de sequência: ajuste pra corrigir

:::

:::step 3. Série da NF-e
O campo **Série** (geralmente "1") identifica a série da nota. Mude só se o contador orientar — a SEFAZ exige que séries sejam usadas de forma consistente.
:::

:::danger
Número de NF-e repetido é **rejeitado pela SEFAZ**. Se errar na numeração, consulte o contador antes de tentar emitir.
:::
`.trim(),
  },

  // ─────────────────────────────────────────────
  // FINANCEIRO
  // ─────────────────────────────────────────────
  {
    id: 'financeiro-contas-receber',
    categoria: 'financeiro',
    icone: '💰',
    titulo: 'Contas a receber',
    resumo: 'Veja o que os clientes devem, filtre por vencimento e dê baixa quando pagar.',
    tags: ['financeiro', 'contas a receber', 'baixa', 'atrasado', 'vencimento', 'receber'],
    conteudo: `
# Contas a receber

Toda venda faturada gera uma (ou mais) **conta a receber**. Aqui você controla o que os clientes devem e marca quando pagam.

:::flow
Venda faturada :: conta a receber criada automaticamente
Monitorar :: filtros por vencimento
Cliente pagou :: dar baixa
Baixa em lote :: pagar vários de uma vez
:::

## Acessar

Vá em **Financeiro → Contas a Receber**.

## Filtros disponíveis (chips coloridos)

| Chip | O que mostra |
|---|---|
| **Atrasados** | Vencidas e não pagas |
| **Hoje** | Vencem hoje |
| **Semana** | Vencem nos próximos 7 dias |
| **Mês** | Vencem neste mês |
| **Personalizado** | Escolha data de início e fim |

## Dar baixa numa conta

:::step 1. Encontrar a conta
Use os filtros ou busque pelo nome do cliente no campo de busca.
:::

:::step 2. Baixar
Clique em **Baixar** na linha da conta. Um painel abre com:
- **Data do recebimento** (padrão: hoje)
- **Valor recebido** (pode ser diferente se houver desconto ou juros)
- **Conta bancária** de destino

Confirme e a conta vai pra coluna **Pagas**.
:::

## Baixa em lote

:::step Selecionar e baixar várias
Marque as caixinhas de várias contas ao mesmo tempo. Um botão **Baixar Selecionadas** aparece no topo. Clique e confirme os dados de recebimento (data e conta). Todas são baixadas de uma vez.
:::

:::tip Crédito de cliente
Se um cliente devolveu produto ou houve desconto, o valor pode virar um **crédito** a favor dele (usado na próxima compra). Veja [Crédito e devolução de clientes](#financeiro-credito-cliente).
:::
`.trim(),
  },

  {
    id: 'financeiro-contas-pagar',
    categoria: 'financeiro',
    icone: '📤',
    titulo: 'Contas a pagar',
    resumo: 'Gerencie o que a empresa deve: fornecedores, guias e despesas.',
    tags: ['financeiro', 'contas a pagar', 'fornecedor', 'baixa', 'boleto', 'codigo barras'],
    conteudo: `
# Contas a pagar

As contas a pagar vêm de três origens principais: **entrada de NF de fornecedor**, **guias fiscais (GNRE)** e **lançamentos manuais**.

## Acessar

Vá em **Financeiro → Contas a Pagar**.

## De onde vêm as contas

| Origem | Quando é criada |
|---|---|
| **Entrada de NF** | Ao importar XML de compra de fornecedor |
| **GNRE** | Ao gerar guia de DIFAL |
| **Manual** | Cadastrada diretamente no financeiro |

## Filtros

Os mesmos chips de **Atrasados / Hoje / Semana / Mês** funcionam aqui. Use pra ver o que vence logo.

## Dar baixa

:::step 1. Encontrar a conta
Busque pelo nome do fornecedor ou pelo número do documento.
:::

:::step 2. Baixar
Clique em **Baixar**. Informe:
- **Data do pagamento**
- **Valor pago**
- **Conta bancária** de onde saiu o dinheiro

Se o boleto tiver código de barras, o sistema consegue identificar a conta automaticamente ao escanear (campo **Código de Barras**).
:::

## Baixa em lote / borderô

Para pagar vários fornecedores de uma vez, use o **Borderô**. Veja [Borderô e pagamentos em lote](#financeiro-bordero).

:::tip
Contas geradas por GNRE já chegam com o valor calculado pelo sistema. Veja [ST e DIFAL](#fiscal-nfe-st-difal) pra entender o cálculo.
:::
`.trim(),
  },

  {
    id: 'financeiro-boletos',
    categoria: 'financeiro',
    icone: '🏦',
    titulo: 'Emissão de boletos e CNAB',
    resumo: 'Gere boletos para os clientes e arquivos de remessa para o banco.',
    tags: ['boleto', 'cnab', 'remessa', 'banco', 'santander', 'caixa', 'financeiro'],
    conteudo: `
# Emissão de boletos e CNAB

O sistema gera **boletos bancários** para as contas a receber e **arquivos CNAB 240** pra enviar pro banco de uma vez.

:::flow
Conta a receber :: criada no faturamento
Gerar boleto :: por conta ou em lote
Arquivo CNAB :: enviar pro banco
Retorno :: banco confirma pagamentos
:::

## Bancos configurados

O sistema trabalha com:
- **Santander** (código 033)
- **Caixa Econômica Federal** (código 104)

Para outros bancos, consulte o administrador.

## Gerar boleto individual

:::step 1. Acessar a conta a receber
Vá em **Financeiro → Contas a Receber** e localize a conta desejada.
:::

:::step 2. Gerar boleto
Clique em **Gerar Boleto** na linha da conta. O boleto é gerado em PDF — você pode imprimir ou enviar ao cliente.
:::

## Gerar boletos em lote

:::step 1. Selecionar contas
Na tela de Contas a Receber, marque as contas desejadas.
:::

:::step 2. Gerar lote
Clique em **Gerar Boletos (Lote)**. O sistema gera todos os boletos e cria o arquivo **CNAB de remessa**.
:::

:::step 3. Enviar ao banco
Salve o arquivo CNAB e envie pelo **internet banking** do banco (upload de arquivo de remessa). O banco processa e depois envia o arquivo de **retorno** com os pagamentos confirmados.
:::

:::tip
Pra ativar os boletos, o administrador precisa configurar os dados bancários (agência, conta, convênio). Veja [Configuração da empresa](#admin-config-empresa).
:::
`.trim(),
  },

  {
    id: 'financeiro-bordero',
    categoria: 'financeiro',
    icone: '📋',
    titulo: 'Borderô e pagamentos em lote',
    resumo: 'Pague vários fornecedores de uma vez com PIX copia-e-cola ou borderô.',
    tags: ['bordero', 'pagamento em lote', 'pix', 'fornecedor', 'financeiro'],
    conteudo: `
# Borderô e pagamentos em lote

Ao invés de pagar fornecedor por fornecedor, junte tudo num **borderô** e pague de uma vez.

:::flow
Selecionar contas a pagar :: as do periodo
Criar borderô :: agrupar num único lote
PIX copia-e-cola :: um por um de forma rápida
PDF com assinaturas :: para autorização interna
:::

## Passo a passo

:::step 1. Acessar Borderô
Vá em **Financeiro → Borderô** (ou **Pagamentos em Lote**).
:::

:::step 2. Selecionar as contas
Escolha o **período** de vencimento e marque as contas a pagar que entrarão no borderô. O sistema mostra o **total** do lote.
:::

:::step 3. Criar o borderô
Clique em **Criar Borderô**. O sistema agrupa todas as contas num único documento de controle.
:::

:::step 4. Pagar via PIX
Para cada conta no borderô, o sistema exibe o **PIX copia-e-cola** (chave ou QR Code). Copie e cole no app do banco pra pagar. Após pagar cada um, clique em **Marcar como pago** no sistema.
:::

:::step 5. Gerar PDF do borderô
Clique em **Gerar PDF**. O documento tem a lista de todos os pagamentos com espaço pra **3 assinaturas** (aprovação interna, financeiro e diretoria, conforme o processo da empresa).
:::
`.trim(),
  },

  {
    id: 'financeiro-credito-cliente',
    categoria: 'financeiro',
    icone: '🔄',
    titulo: 'Crédito e devolução de clientes',
    resumo: 'Quando o cliente devolve produto, o valor vira crédito pra usar na próxima compra.',
    tags: ['credito', 'devolucao', 'cliente', 'troca', 'financeiro', 'saldo'],
    conteudo: `
# Crédito e devolução de clientes

Quando um cliente devolve um produto ou há um acerto a favor dele, o valor vira um **crédito** que pode ser usado na próxima compra.

:::flow
Devolução :: cliente devolve produto
Crédito criado :: valor entra no saldo do cliente
Próxima compra :: desconta automaticamente
:::

## Como funciona

Ao **cancelar um pedido faturado** ou registrar uma devolução, o sistema cria automaticamente um **Crédito de Cliente** com o valor correspondente.

## Ver o saldo de crédito de um cliente

:::step 1. Abrir o cadastro
Vá em **Cadastros → Clientes** e abra o cliente.
:::

:::step 2. Ver saldo
Na aba **Financeiro** (ou **Créditos**) do cadastro, aparece o saldo de crédito disponível.
:::

## Usar o crédito numa venda

:::step Aplicar ao pedido
Ao criar um novo pedido para o cliente, o sistema avisa que ele tem crédito. Clique em **Usar Crédito** e o valor é descontado do total da venda. Se o crédito for maior que a venda, o saldo continua pra próxima.
:::

:::tip
Crédito de cliente é diferente de desconto. O desconto reduz o preço do item; o crédito é um saldo que o cliente já tem a receber da empresa.
:::
`.trim(),
  },

  {
    id: 'financeiro-fluxo-dre',
    categoria: 'financeiro',
    icone: '📈',
    titulo: 'Fluxo de caixa e DRE',
    resumo: 'Visão da saúde financeira: o que entrou, o que saiu e o resultado.',
    tags: ['fluxo de caixa', 'dre', 'resultado', 'financeiro', 'controladoria', 'relatorio'],
    conteudo: `
# Fluxo de caixa e DRE

A **Controladoria** reúne os principais indicadores financeiros num só lugar: fluxo de caixa, DRE (Demonstrativo de Resultado), margem e evolução de vendas.

## Como acessar

Vá em **Financeiro → Controladoria**.

## O que tem lá

| Aba | O que mostra |
|---|---|
| **Visão Geral** | KPIs do período: faturamento, despesas, resultado |
| **Evolução** | Gráfico de vendas mês a mês |
| **Margem** | Margem bruta por produto ou período |
| **Marketplace** | Comparativo de canais de venda |
| **Despesas** | Categorias de custos |
| **Fluxo + Aging** | Entradas e saídas projetadas + contas vencidas |

## Fluxo de Caixa

Mostra, dia a dia, o que deve **entrar** (contas a receber com vencimento) e o que deve **sair** (contas a pagar). Use pra planejar o caixa da semana.

## DRE

O **Demonstrativo de Resultado do Exercício** mostra:

- **Receita bruta** (total vendido)
- **(-) Devoluções e descontos**
- **= Receita líquida**
- **(-) Custo das mercadorias (CMV)**
- **= Margem bruta**
- **(-) Despesas operacionais**
- **= Resultado do período**

:::tip
Para uma análise mais detalhada, use os filtros de **período** e **categoria** dentro da Controladoria. O gráfico atualiza na hora.
:::
`.trim(),
  },

  // ─────────────────────────────────────────────
  // ESTOQUE
  // ─────────────────────────────────────────────
  {
    id: 'estoque-visao-geral',
    categoria: 'estoque',
    icone: '📦',
    titulo: 'Estoque — visão geral e movimentos',
    resumo: 'Como ver o saldo atual, movimentos e posição por produto.',
    tags: ['estoque', 'saldo', 'movimentos', 'posicao', 'produto', 'deposito'],
    conteudo: `
# Estoque — visão geral e movimentos

O estoque é atualizado automaticamente a cada entrada, saída, ajuste ou transferência. Você não precisa mexer manualmente na maioria dos casos.

## Como ver o saldo atual

Vá em **Estoque → Posição de Estoque**. Aparece a lista de produtos com:
- **Saldo atual** em cada depósito
- **Estoque mínimo** (se configurado)
- Indicação de itens **abaixo do mínimo** (em destaque)

## Ver movimentos de um produto

:::step 1. Acessar
Vá em **Estoque → Movimentos**.
:::

:::step 2. Filtrar
Use os filtros de **produto**, **período** e **tipo de movimento** (entrada, saída, ajuste, transferência).
:::

:::step 3. Ler os movimentos
| Tipo | O que significa |
|---|---|
| **Entrada** | Produto entrou (compra ou ajuste positivo) |
| **Saída** | Produto saiu (venda faturada ou ajuste negativo) |
| **Ajuste** | Correção manual de contagem |
| **Transferência** | Moveu de um depósito pra outro |

## Depósitos

Um produto pode estar em **depósitos diferentes** (ex.: Depósito Central, Câmara Fria). O saldo é separado por depósito. Ao faturar, o sistema desconta do depósito configurado na venda.

:::tip Sincronização com WinThor
O sistema pode importar o estoque do WinThor automaticamente. Fale com o administrador se precisar atualizar.
:::
`.trim(),
  },

  {
    id: 'estoque-entrada-nf',
    categoria: 'estoque',
    icone: '📥',
    titulo: 'Entrada de mercadoria por nota fiscal',
    resumo: 'Importe o XML do fornecedor para dar entrada no estoque e criar a conta a pagar.',
    tags: ['estoque', 'entrada', 'nf entrada', 'xml', 'fornecedor', 'compra', 'conta a pagar'],
    conteudo: `
# Entrada de mercadoria por nota fiscal

Quando a mercadoria chega com nota fiscal do fornecedor, você importa o XML no sistema. Isso **soma o estoque** e cria a **conta a pagar** automaticamente.

:::flow
Fornecedor envia :: XML da NF-e
Importar XML :: Estoque → Entrada NF
Conferir itens :: bate com o pedido?
Confirmar :: estoque sobe + conta a pagar criada
:::

## Passo a passo

:::step 1. Acessar Entrada de NF
Vá em **Estoque → Entrada (NF)**.
:::

:::step 2. Importar o XML
Clique em **Importar XML**. Selecione o arquivo **.xml** da nota do fornecedor. O sistema lê o arquivo e preenche automaticamente:
- Fornecedor
- Itens e quantidades
- Valores e impostos
- Data de emissão
:::

:::step 3. Conferir os dados
Revise os itens e quantidades. Verifique se os **produtos** foram reconhecidos corretamente (o sistema tenta casar pelo código de barras ou código interno). Se algum produto não for encontrado, vincule manualmente.
:::

:::step 4. Confirmar a entrada
Clique em **Confirmar Entrada**. O sistema:
- Soma o estoque dos itens recebidos
- Cria a conta a pagar com o valor e vencimento da nota
- Registra o movimento com o número da NF

:::

:::tip Entrada sem nota
Se a mercadoria chegou sem nota (fornecedor informal), use a **Entrada Avulsa** na mesma tela. Ela soma o estoque mas **não cria conta a pagar** (lançamento manual separado).
:::

:::warning Entrada em dobro
Se por algum motivo você importar o mesmo XML duas vezes, o sistema pode criar duplicidade. Fique atento ao número da NF — o sistema avisa se a chave de acesso já foi importada.
:::
`.trim(),
  },

  {
    id: 'estoque-ajuste',
    categoria: 'estoque',
    icone: '🔧',
    titulo: 'Ajuste de estoque',
    resumo: 'Corrija diferenças de contagem com motivo registrado e histórico.',
    tags: ['estoque', 'ajuste', 'contagem', 'inventario', 'motivo', 'historico'],
    conteudo: `
# Ajuste de estoque

A contagem física revelou diferença? Use o ajuste de estoque pra corrigir — sempre com **motivo** registrado. Tudo fica no histórico pra auditoria.

:::flow
Contar fisicamente :: o que tem no depósito
Acessar ajuste :: Estoque → Ajuste de Estoque
Informar :: produto, quantidade real e motivo
Confirmar :: saldo atualizado + histórico registrado
:::

## Modos de ajuste

**Contagem (absoluto)**: você informa a **quantidade real** que contou. O sistema calcula a diferença automaticamente.

**Diferença (relativo)**: você informa só o **quanto vai somar ou subtrair** (ex.: +10 ou -5).

## Passo a passo

:::step 1. Acessar
Vá em **Estoque → Ajuste de Estoque**.
:::

:::step 2. Selecionar o produto
Busque o produto pelo nome ou código de barras.
:::

:::step 3. Informar a quantidade
Escolha o modo (contagem ou diferença) e informe o valor.
:::

:::step 4. Informar o motivo
Escolha uma das opções de motivo ou escreva uma descrição:
- Quebra / avaria
- Diferença de contagem
- Produto vencido
- Erro de entrada anterior
- Outros (descreva)
:::

:::step 5. Conta gerencial
O ajuste pode ser vinculado a uma **conta gerencial** (ex.: "Quebras e Avarias") pra aparecer no DRE como custo.
:::

:::step 6. Confirmar
Clique em **Confirmar Ajuste**. O saldo é atualizado e o registro aparece no histórico com nome do usuário, data e hora.
:::

:::warning
Ajuste de estoque não é lugar pra "fazer sumir" diferença sem explicar. O motivo fica registrado e pode ser auditado. Use com responsabilidade.
:::
`.trim(),
  },

  {
    id: 'estoque-transferencia',
    categoria: 'estoque',
    icone: '🔀',
    titulo: 'Transferência entre filiais',
    resumo: 'Mova mercadoria de uma filial para outra com rastreamento completo.',
    tags: ['estoque', 'transferencia', 'filial', 'deposito', 'intercompany'],
    conteudo: `
# Transferência entre filiais

Precisa mandar produto de uma filial pra outra? O sistema registra a transferência e atualiza o estoque nas duas pontas.

:::flow
Filial origem :: tem o produto
Criar transferência :: Estoque → Transferência
Informar destino :: qual filial vai receber
Confirmar :: saldo diminui na origem e sobe no destino
:::

## Passo a passo

:::step 1. Acessar
Vá em **Estoque → Transferência entre Filiais**.
:::

:::step 2. Informar origem e destino
Selecione a **filial de origem** (de onde sai) e a **filial de destino** (pra onde vai). Se você já estiver logado na filial de origem, ela já vem selecionada.
:::

:::step 3. Adicionar os itens
Busque cada produto e informe a quantidade a transferir. O sistema mostra o saldo disponível na origem pra você não errar.
:::

:::step 4. Confirmar
Clique em **Confirmar Transferência**. O estoque da filial de origem **diminui** e da filial de destino **aumenta** instantaneamente. O movimento fica registrado em ambas.
:::

:::tip NF-e de transferência
Transferências entre filiais com CNPJs diferentes podem exigir NF-e fiscal. Consulte o contador — o sistema está preparado para isso quando o certificado da filial estiver configurado.
:::
`.trim(),
  },

  // ─────────────────────────────────────────────
  // COMODATO
  // ─────────────────────────────────────────────
  {
    id: 'comodato-o-que-e',
    categoria: 'comodato',
    icone: '🧊',
    titulo: 'O que é comodato de freezers',
    resumo: 'Entenda como funciona o empréstimo de freezers e por que controlar no sistema.',
    tags: ['comodato', 'freezer', 'emprestimo', 'basico', 'o que e'],
    conteudo: `
# O que é comodato de freezers

**Comodato** é o empréstimo de um freezer para o cliente — ele usa no ponto de venda dele, mas o freezer **continua sendo da Velore**.

Controlar no sistema é fundamental porque:
- Você sabe **onde cada freezer está** a qualquer momento
- Garante que os freezers **voltam** quando o cliente para de comprar
- Mantém o **histórico** de envio e retorno de cada equipamento
- Gera **documentos fiscais** (NF-e de remessa e retorno) exigidos pela legislação

## O fluxo do comodato

:::flow
Cadastrar o freezer :: serie e patrimônio
Remessa :: freezer vai pro cliente com NF-e
Em uso :: vinculado ao cliente, fora da empresa
Retorno :: cliente devolve, NF-e de retorno
Disponível :: freezer volta ao estoque
:::

## Quanto tempo pode ficar fora?

O sistema registra a **data de remessa**. Pelo dashboard de comodato, você vê quais freezers estão há mais tempo no cliente — útil pra fazer visitas de manutenção ou retirada.

## A lei exige documentação fiscal

Enviar freezer sem NF-e de remessa é irregularidade fiscal. O sistema já gera automaticamente a NF-e com o **CFOP 5908** (remessa para comodato) e na volta com **CFOP 1908** (retorno de comodato).

:::tip
Se o cliente for de outro estado, o CFOP muda (6908 / 2908). O sistema ajusta automaticamente com base na UF do cliente.
:::

[Como cadastrar e enviar um freezer](#comodato-cadastrar-enviar)
`.trim(),
  },

  {
    id: 'comodato-cadastrar-enviar',
    categoria: 'comodato',
    icone: '📤',
    titulo: 'Cadastrar e enviar um freezer (remessa)',
    resumo: 'Cadastre o freezer no sistema e gere a NF-e de remessa ao enviar pro cliente.',
    tags: ['comodato', 'freezer', 'cadastrar', 'remessa', 'nfe', 'cfop 5908'],
    conteudo: `
# Cadastrar e enviar um freezer (remessa)

Antes de enviar um freezer a um cliente, ele precisa estar cadastrado no sistema.

## Cadastrar o freezer

:::step 1. Acessar
Vá em **Comodato → Freezers** (ou **Equipamentos**).
:::

:::step 2. Novo freezer
Clique em **+ Novo Freezer** e preencha:
- **Número de série** (da etiqueta no equipamento)
- **Patrimônio** (número interno da empresa, se houver)
- **Modelo / Descrição** (ex.: "Freezer Vertical 400L Branco")
- **Capacidade** (litros)
- **Status**: Disponível (recém cadastrado)
:::

:::step 3. Salvar
Clique em **Salvar**. O freezer aparece no sistema como **Disponível**.
:::

## Enviar ao cliente (remessa)

:::step 1. Criar remessa
Vá em **Comodato → Remessas** e clique em **+ Nova Remessa**.
:::

:::step 2. Selecionar o cliente
Busque e selecione o cliente que vai receber o freezer.
:::

:::step 3. Selecionar o freezer
O sistema mostra os freezers com status **Disponível**. Selecione o que vai ser enviado. Dá pra enviar mais de um ao mesmo cliente.
:::

:::step 4. Gerar a NF-e de remessa
Clique em **Gerar NF de Remessa**. A nota sai com **CFOP 5908** (ou 6908 se for pra outro estado). O freezer muda de status pra **Em comodato** e fica vinculado ao cliente.
:::

:::step 5. Imprimir o contrato
Após a remessa, clique em **Gerar Contrato PDF**. O documento lista as condições do comodato e tem espaço pra assinatura do cliente. Guarde o original assinado.
:::
`.trim(),
  },

  {
    id: 'comodato-retorno-dashboard',
    categoria: 'comodato',
    icone: '📊',
    titulo: 'Retorno de freezer e dashboard de comodato',
    resumo: 'Registre a devolução do freezer e acompanhe todos no mapa.',
    tags: ['comodato', 'retorno', 'dashboard', 'freezer', 'devolucao', 'nfe', 'cfop 1908'],
    conteudo: `
# Retorno de freezer e dashboard de comodato

## Registrar o retorno de um freezer

:::step 1. Acessar
Vá em **Comodato → Retornos** e clique em **+ Novo Retorno**.
:::

:::step 2. Selecionar o cliente e freezer
Busque o cliente. O sistema mostra todos os freezers **em comodato** com esse cliente. Selecione o que está voltando.
:::

:::step 3. Informar a condição
Registre o estado do freezer:
- **Bom** — volta normal pro estoque
- **Avariado** — vai pra manutenção
- **Irrecuperável** — baixa do ativo
:::

:::step 4. Gerar NF-e de retorno
Clique em **Gerar NF de Retorno**. A nota sai com **CFOP 1908** (ou 2908 se vier de outro estado). O freezer volta ao status **Disponível** (ou **Em manutenção** se avariado).
:::

## Dashboard de comodato

Vá em **Comodato → Dashboard**. Você verá:

| Painel | O que mostra |
|---|---|
| **Total de freezers** | Quantos existem no cadastro |
| **Em comodato** | Quantos estão na rua agora |
| **Disponíveis** | Prontos pra nova remessa |
| **Em manutenção** | Avariados aguardando reparo |
| **Mais tempo fora** | Os que estão com cliente há mais tempo |

:::tip
Use o dashboard regularmente pra identificar freezers que estão há muito tempo sem retorno e acionar os clientes.
:::
`.trim(),
  },

  // ─────────────────────────────────────────────
  // CADASTROS
  // ─────────────────────────────────────────────
  {
    id: 'cadastros-cliente',
    categoria: 'cadastros',
    icone: '👤',
    titulo: 'Cadastrar cliente',
    resumo: 'Preencha os dados certos para que a venda e o imposto saiam corretamente.',
    tags: ['cadastro', 'cliente', 'cpf', 'cnpj', 'uf', 'rota', 'credito', 'fiscal'],
    conteudo: `
# Cadastrar cliente

Um cliente bem cadastrado faz tudo funcionar certo: imposto correto, entrega no lugar certo, crédito sob controle.

:::flow
Cadastros → Clientes :: menu principal
+ Novo Cliente :: preencher os dados
UF e cidade :: muito importante pro fiscal
Salvar :: pronto pra usar
:::

## Passo a passo

:::step 1. Acessar
Vá em **Cadastros → Clientes** e clique em **+ Novo Cliente**.
:::

:::step 2. Dados básicos
- **Nome / Razão Social**
- **CPF** (pessoa física) ou **CNPJ** (empresa)
- **Inscrição Estadual** — se for empresa revendedora, preencha a IE. Sem IE, o sistema trata como consumidor final (e pode calcular DIFAL)
- **Telefone / WhatsApp**
- **E-mail**
:::

:::step 3. Endereço — preste atenção na UF
O endereço define o imposto:
- **CEP**: preencha e o sistema busca o endereço automaticamente
- **Cidade** e **UF**: confirme que está correto
- Clientes de **UF diferente do CE** podem gerar DIFAL. Veja [ST e DIFAL](#fiscal-nfe-st-difal)
:::

:::step 4. Dados comerciais
- **Rota**: define em qual carga o cliente vai entrar
- **Tabela de preço**: qual tabela de preço se aplica a esse cliente
- **Vendedor**: o vendedor responsável pela conta
- **Limite de crédito**: valor máximo de saldo em aberto que o cliente pode ter
:::

:::step 5. Salvar
Clique em **Salvar**. O cliente já aparece pra selecionar no próximo pedido.
:::

:::tip Atalhos
- **Enter avança** para o próximo campo — não precisa do mouse
- **Duplicar**: abra um cliente parecido e clique em **Duplicar** pra criar um novo com os mesmos dados base (mais rápido que digitar tudo)
- **Código automático**: o código do cliente é gerado automaticamente, não precisa preencher
:::
`.trim(),
  },

  {
    id: 'cadastros-produto',
    categoria: 'cadastros',
    icone: '🍦',
    titulo: 'Cadastrar produto',
    resumo: 'Nome, código de barras, preço, NCM e CFOP — o que não pode faltar.',
    tags: ['cadastro', 'produto', 'codigo de barras', 'ncm', 'cfop', 'preco', 'estoque minimo'],
    conteudo: `
# Cadastrar produto

Para a nota fiscal sair certa e o estoque funcionar, o produto precisa ter os dados fiscais corretos.

:::flow
Cadastros → Produtos :: menu principal
+ Novo Produto :: preencher dados
Aba Geral :: NCM e CFOP obrigatórios
Salvar :: pronto pra vender
:::

## Passo a passo

:::step 1. Acessar
Vá em **Cadastros → Produtos** e clique em **+ Novo Produto**.
:::

:::step 2. Dados básicos
- **Descrição**: nome completo do produto como vai aparecer na nota
- **Código interno**: gerado automaticamente (mas pode personalizar)
- **Código de barras (EAN)**: o número embaixo do código de barras da embalagem. Importantíssimo pro PDV e pra entrada de NF
- **Unidade de medida**: UN, CX, KG, etc.
- **Categoria / Grupo**: agrupa pra relatórios
:::

:::step 3. Preço e custo
- **Preço de venda**: valor que aparece na venda (pode ter tabelas diferentes por cliente)
- **Custo**: valor de compra (importante pro cálculo de margem e DRE)
- **Estoque mínimo**: o sistema avisa quando cair abaixo desse valor
:::

:::step 4. Dados fiscais (aba Geral)
Esta é a aba mais importante pro fisco:
- **NCM**: código de 8 dígitos que classifica o produto. Sem NCM a nota não sai. Peça pro contador se não souber
- **CFOP**: código da operação (ex.: 5102 para venda dentro do estado). O sistema sugere, mas confirme com o contador
- **Origem**: nacional, importado etc.
- **ST / ICMS**: configurações de substituição tributária (preencha só se o contador orientar)
:::

:::step 5. Salvar
Clique em **Salvar**. O produto já aparece pra buscar nas vendas e no PDV.
:::

:::tip Duplicar produto
Para criar produtos parecidos (ex.: sorvete de vários sabores com mesmo NCM), abra um produto e clique em **Duplicar**. Altere só o que muda (sabor, código de barras).
:::
`.trim(),
  },

  {
    id: 'cadastros-tabela-preco',
    categoria: 'cadastros',
    icone: '💲',
    titulo: 'Tabelas de preço e precificação em massa',
    resumo: 'Crie tabelas diferentes por cliente e ajuste preços de toda a tabela de uma vez.',
    tags: ['tabela de preco', 'precificacao', 'reajuste', 'desconto', 'preco em massa'],
    conteudo: `
# Tabelas de preço e precificação em massa

Você pode ter **tabelas de preço diferentes** para tipos de clientes distintos (ex.: varejo, atacado, distribuidor).

## Criar uma tabela de preço

:::step 1. Acessar
Vá em **Cadastros → Tabelas de Preço**.
:::

:::step 2. Nova tabela
Clique em **+ Nova Tabela**. Dê um nome (ex.: "Varejo CE") e selecione a tabela base se for derivar de outra.
:::

:::step 3. Preencher os preços
Para cada produto, informe o preço nessa tabela. Ou use a opção **Derivar da tabela base com margem**: informe o percentual (ex.: +15%) e o sistema calcula automaticamente.
:::

:::step 4. Vincular ao cliente
No cadastro do cliente, selecione essa tabela no campo **Tabela de Preço**. Dali em diante, esse cliente vê os preços dessa tabela ao criar pedidos.
:::

## Reajuste em massa

Precisa aumentar ou diminuir todos os preços de uma tabela?

:::step 1. Acessar Precificação
Vá em **Cadastros → Precificação** (ou **Precificação em Massa**).
:::

:::step 2. Selecionar a tabela
Escolha qual tabela vai ajustar.
:::

:::step 3. Tipo de ajuste
- **Por percentual**: ex. +10% aumenta todos os preços em 10%
- **Por valor fixo**: ex. +R$ 2,00 em todos os itens

Informe se é aumento (+) ou redução (-).
:::

:::step 4. Filtrar (opcional)
Dá pra aplicar só a produtos de um **grupo** ou **categoria** específica.
:::

:::step 5. Pré-visualizar e confirmar
O sistema mostra uma prévia dos novos preços. Revise e clique em **Aplicar Reajuste**.
:::

:::tip ST no preço
Se o produto tem Substituição Tributária (ST), ative a flag **Acrescenta ST** e informe o percentual. O preço de venda já sai com o ST embutido.
:::
`.trim(),
  },

  // ─────────────────────────────────────────────
  // SOFIA
  // ─────────────────────────────────────────────
  {
    id: 'sofia-como-usar',
    categoria: 'sofia',
    icone: '🤖',
    titulo: 'Como usar a Sofia',
    resumo: 'A assistente que responde dúvidas do sistema em linguagem simples.',
    tags: ['sofia', 'ia', 'chat', 'ajuda', 'assistente', 'duvida'],
    conteudo: `
# Como usar a Sofia

A **Sofia** é a assistente inteligente do sistema Velore. Você escreve a pergunta do jeito que fala — sem precisar saber o nome técnico das coisas — e ela responde com o passo a passo.

:::flow
Abrir :: botão roxo no topo da tela
Escrever :: sua dúvida em linguagem simples
Resposta :: passo a passo personalizado
Artigo :: clique pra ver mais detalhes
:::

## Como abrir a Sofia

Clique no **botão roxo** com o ícone da Sofia no canto superior da tela. Ela aparece em qualquer página do sistema.

Ou, dentro da **Central de Ajuda**, use o campo **"Pergunte à Sofia"** na parte de baixo.

## Exemplos do que perguntar

- "Como faço uma venda fiscal pro Piauí?"
- "Onde fica o ajuste de estoque?"
- "Como dou baixa em várias contas ao mesmo tempo?"
- "O que acontece quando fatura um pedido?"
- "Como o entregador registra uma entrega sem internet?"
- "Como emito a GNRE?"
- "O que é comodato?"
- "Como faço pra cancelar uma nota fiscal?"

## O que a Sofia sabe

A Sofia busca nos artigos da **Central de Ajuda** e monta uma resposta personalizada pra você. Ela conhece todas as funcionalidades do sistema descritas nessa central.

:::info
A Sofia não tem acesso aos seus dados pessoais (pedidos, clientes, valores de venda). Ela responde apenas sobre como **usar o sistema**.
:::

:::tip
No final de cada artigo da Central de Ajuda há um botão **"Abrir chat com a Sofia"**. Se o artigo não resolveu, clique nele — sua dúvida vai direto pro chat dela.
:::
`.trim(),
  },

  {
    id: 'sofia-exemplos-uso',
    categoria: 'sofia',
    icone: '💡',
    titulo: 'O que a Sofia pode fazer por você',
    resumo: 'Exemplos práticos de como a Sofia ajuda no dia a dia.',
    tags: ['sofia', 'exemplos', 'ia', 'roteirizacao', 'ajuda', 'duvidas'],
    conteudo: `
# O que a Sofia pode fazer por você

A Sofia não é só pra responder dúvidas — ela também **monta cargas** e **sugere compras**.

## Responder dúvidas sobre o sistema

Pergunte qualquer coisa sobre as telas, como usar funções, onde fica cada coisa. Ela explica em linguagem simples.

**Exemplos:**
- "Como faturar vários pedidos de uma vez?"
- "Onde vejo o histórico de movimentos de um produto?"
- "Como cadastro um novo motorista?"

## Montar a carga (roteirização)

A Sofia usa inteligência artificial pra organizar os pedidos do dia da melhor forma:

- Agrupa pedidos por **rota** e **veículo**
- Sugere a **ordem de entrega** mais eficiente
- Leva em conta a **capacidade do veículo** e a **localização dos clientes**

Para isso, vá em **Roteirização** e clique em **Montar Carga com a Sofia**.

## Sugestão de compras

A Sofia analisa o histórico de vendas pra sugerir o que comprar:

- Identifica produtos com **estoque baixo ou zerado**
- Cruza com o **giro de vendas** dos últimos dias
- Sugere quantidades com base na demanda

Acesse em **Estoque → Sugestão de Compra** e deixe a Sofia calcular.

:::tip
A Sofia melhora com o tempo. Quanto mais o sistema é usado, mais precisa fica a sugestão de compras e a roteirização.
:::
`.trim(),
  },

  // ─────────────────────────────────────────────
  // ADMIN / CONFIGURAÇÃO
  // ─────────────────────────────────────────────
  {
    id: 'admin-config-empresa',
    categoria: 'admin',
    icone: '⚙️',
    titulo: 'Configuração da empresa',
    resumo: 'CNPJ, regime fiscal, certificado A1, numeração de NF e ambiente.',
    tags: ['configuracao', 'empresa', 'cnpj', 'regime', 'certificado', 'numeracao', 'admin'],
    conteudo: `
# Configuração da empresa

Acesso exclusivo de **administrador**. Fica em **Configurações → Empresa**.

:::flow
Dados :: CNPJ, IE, endereço, regime
Certificado :: A1 pra emitir NF-e
Numeração :: próximo número da NF
Ambiente :: homologação ou produção
Bancos :: configurar boletos
:::

## Dados da empresa

:::step 1. Dados básicos
- **Razão Social** e **Nome Fantasia**
- **CNPJ** e **Inscrição Estadual**
- **Endereço completo** (CEP, Rua, Número, Cidade, UF)
- **Telefone** e **E-mail**
:::

:::step 2. Regime tributário / CRT
- **1 — Simples Nacional**: empresa no Simples
- **3 — Regime Normal**: Lucro Presumido ou Real

O regime define os códigos fiscais usados na NF-e (CSOSN pra Simples, CST pra Normal). Se estiver errado, a nota é rejeitada pela SEFAZ.

:::warning
O regime/CRT é definido pelo CNPJ da empresa. Verifique no site da Receita Federal se estiver em dúvida. Um regime errado trava a emissão de notas.
:::
:::

:::step 3. Numeração da NF-e
Na aba **Fiscal**, o campo **Próximo número da NF-e** define de onde a próxima nota começa. Só **admin** edita.

Use pra continuar de onde o sistema antigo parou — número repetido é rejeitado pela SEFAZ.
:::

:::step 4. Ambiente fiscal
- **Homologação**: ambiente de teste, notas sem valor fiscal (ideal pra treinamento)
- **Produção**: notas reais

Só mude pra Produção quando tudo estiver configurado e testado.
:::

## Subir o certificado A1

Veja o artigo [Certificado A1 e numeração da NF-e](#fiscal-certificado-numeracao) pra o passo a passo completo.
`.trim(),
  },

  {
    id: 'admin-usuarios',
    categoria: 'admin',
    icone: '👥',
    titulo: 'Usuários e permissões',
    resumo: 'Cadastre usuários, defina o que cada um pode ver e fazer no sistema.',
    tags: ['admin', 'usuarios', 'permissoes', 'acesso', 'senha', 'perfil'],
    conteudo: `
# Usuários e permissões

Controle quem acessa o sistema e o que cada pessoa pode fazer. Recomendado: cada funcionário tem seu próprio login.

:::flow
Configurações → Usuários :: menu admin
+ Novo Usuário :: preencher dados
Perfil de acesso :: define o que pode usar
Salvar :: usuário recebe o acesso
:::

## Cadastrar um usuário

:::step 1. Acessar
Vá em **Configurações → Usuários** e clique em **+ Novo Usuário**.
:::

:::step 2. Dados do usuário
- **Nome**
- **E-mail** (vai ser o login)
- **Senha** inicial (o usuário pode trocar depois)
- **Perfil / Cargo**: define as permissões automaticamente (Vendedor, Financeiro, Estoque, Administrador)
:::

:::step 3. Permissões detalhadas
Após escolher o perfil base, dá pra ajustar permissões específicas:
- Pode ver relatórios?
- Pode cancelar pedidos?
- Pode editar preços?
- Pode acessar configurações?

Desmarque o que esse usuário **não deve** ter acesso.
:::

:::step 4. Filial de acesso
Se a empresa tem várias filiais, defina em quais filiais esse usuário pode operar.
:::

:::step 5. Salvar
Clique em **Salvar**. O usuário já pode entrar com o e-mail e senha cadastrados.
:::

:::tip Redefinir senha
Se um usuário esquecer a senha, o admin pode redefinir em **Configurações → Usuários → (usuário) → Redefinir Senha**.
:::

:::warning
Nunca compartilhe a senha de administrador. Cada pessoa deve ter seu próprio login — isso garante rastreabilidade (quem fez o quê e quando).
:::
`.trim(),
  },

  {
    id: 'admin-filiais',
    categoria: 'admin',
    icone: '🏢',
    titulo: 'Filiais e multi-CNPJ',
    resumo: 'Configure filiais com CNPJs diferentes, depósitos e permissões por filial.',
    tags: ['admin', 'filial', 'cnpj', 'deposito', 'multi-filial', 'fiscal', 'financeiro'],
    conteudo: `
# Filiais e multi-CNPJ

O sistema suporta **múltiplas filiais** com CNPJs distintos. Cada filial pode ter seu próprio certificado, numeração de NF e depósito de estoque.

## Conceito: filial fiscal vs filial financeira

| Tipo | O que faz |
|---|---|
| **Filial fiscal** | Emite a nota fiscal, baixa o estoque do depósito dela |
| **Filial financeira** | Recebe o lançamento no caixa, gerencia as contas |

Numa venda, você pode emitir a nota pela filial A (fiscal) mas o dinheiro entrar no caixa da filial B (financeira). Isso é comum quando há uma filial operacional e uma financeira.

## Cadastrar uma filial

:::step 1. Acessar
Vá em **Configurações → Filiais** e clique em **+ Nova Filial**.
:::

:::step 2. Dados da filial
- **Razão Social** e **CNPJ**
- **Inscrição Estadual**
- **Endereço completo**
- **Regime tributário** (pode ser diferente da matriz)
:::

:::step 3. Certificado e numeração
Cada filial tem seu próprio:
- **Certificado A1** (se emitir NF-e)
- **Próximo número da NF-e**
- **Série** da nota

Preencha na aba **Fiscal** da filial.
:::

:::step 4. Depósito
Vincule um (ou mais) **depósito(s)** a essa filial. O depósito é onde o estoque físico fica. Ao faturar pela filial, o estoque baixa do depósito dela.
:::

:::tip
Para o [fechamento do PDV](#pdv-fechamento), você pode selecionar qual filial está fechando. Assim cada filial tem seu próprio caixa separado.
:::
`.trim(),
  },

  {
    id: 'admin-formas-pagamento',
    categoria: 'admin',
    icone: '💳',
    titulo: 'Formas de pagamento',
    resumo: 'Configure e ordene as formas de pagamento disponíveis no PDV e nos pedidos.',
    tags: ['admin', 'formas de pagamento', 'ordenar', 'pix', 'boleto', 'dinheiro', 'configuracao'],
    conteudo: `
# Formas de pagamento

Configure quais formas de pagamento aparecem no PDV e nas vendas, e em qual ordem.

## Acessar

Vá em **Configurações → Formas de Pagamento**.

## Adicionar uma forma

:::step 1. Nova forma
Clique em **+ Nova Forma**. Preencha:
- **Nome** (ex.: "PIX", "Boleto 30 dias", "Cartão de Crédito")
- **Tipo**: dinheiro, cartão, boleto, PIX, cheque, crédito
- **Prazo padrão** (em dias): 0 pra à vista
- **Ativo**: se vai aparecer nas telas
:::

:::step 2. Salvar
Clique em **Salvar**. A nova forma já aparece nas opções de pagamento.
:::

## Ordenar as formas (arrastar)

A **ordem** das formas define como elas aparecem no PDV e no checkout. Pra ordenar:

1. Na lista de formas de pagamento, segure e **arraste** a forma para a posição desejada
2. Solte — a ordem é salva automaticamente

Coloque as formas mais usadas no topo (ex.: PIX e Dinheiro).

:::tip
O administrador pode **desativar** formas que não são mais usadas sem apagá-las. Assim o histórico de vendas antigas permanece correto.
:::
`.trim(),
  },

  {
    id: 'admin-depositos',
    categoria: 'admin',
    icone: '🏭',
    titulo: 'Depósitos de estoque',
    resumo: 'Cadastre os locais físicos onde o estoque é guardado.',
    tags: ['admin', 'deposito', 'estoque', 'armazem', 'filial', 'configuracao'],
    conteudo: `
# Depósitos de estoque

Um **depósito** representa um local físico onde a mercadoria fica (ex.: "Câmara Fria 1", "Depósito Principal", "Expositores Loja").

## Por que ter depósitos separados?

- Controle por **local físico** dentro da empresa
- **Filiais** diferentes têm depósitos diferentes
- Saber exatamente **onde está cada produto** (câmara fria x temperatura ambiente)

## Cadastrar um depósito

:::step 1. Acessar
Vá em **Configurações → Depósitos** e clique em **+ Novo Depósito**.
:::

:::step 2. Preencher
- **Nome** (ex.: "Câmara Fria Principal")
- **Filial** a que pertence
- **Descrição** (opcional)
- **Depósito padrão**: marque se quiser que ele seja selecionado automaticamente nas vendas

Clique em **Salvar**.
:::

## Depósito padrão de venda

Ao faturar um pedido, o sistema desconta o estoque do **depósito padrão** da filial. Para mudar em casos específicos, selecione o depósito no momento do faturamento.

:::tip
Se você tem produtos refrigerados e produtos em temperatura ambiente, crie dois depósitos separados. Isso facilita o controle e evita confusão no inventário.
:::
`.trim(),
  },
]
